/**
 * Edit per-locale template overrides for one string row on a template (optional; empty = use global).
 */

import React, { useEffect, useState, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress
} from "@mui/material";
import { useGlobalConfig } from "context/GlobalContext";
import { BglPrimarySmallButton, BglSecondarySmallButton } from "components/button/BglButtons";
import { StringRegistryApis } from "../api/stringRegistryAPI";
import { StringTemplate } from "../types/stringRegistryTypes";
import { getLocaleValue, normalizeLocaleCode } from "../utils/localeDisplay";
import { getGlobalLocaleOverrides } from "../utils/globalTemplateLocales";

type PublishedLocale = { code: string; name: string };

interface Props {
  open: boolean;
  onClose: () => void;
  template: StringTemplate;
  itemIndex: number | null;
  localeCatalog: PublishedLocale[];
  onSaved: () => void;
}

export const EditTemplateLocaleOverridesDialog = ({
  open,
  onClose,
  template,
  itemIndex,
  localeCatalog,
  onSaved
}: Props) => {
  const { rc } = useGlobalConfig();
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validLocales = useMemo(
    () => localeCatalog.filter((l) => Boolean(normalizeLocaleCode(l?.code))),
    [localeCatalog]
  );

  const item = itemIndex != null ? template.items?.[itemIndex] : undefined;
  const entry = item?.stringRegistryEntry;

  useEffect(() => {
    if (!open || itemIndex == null) {
      setDraft({});
      setError(null);
      return;
    }
    const row = template.items?.[itemIndex];
    if (!row) {
      setDraft({});
      return;
    }
    const next: Record<string, string> = {};
    for (const { code } of validLocales) {
      const k = normalizeLocaleCode(code);
      const ov = row.localeOverrides?.find((o) => normalizeLocaleCode(o.locale) === k);
      next[k] = ov ? String(ov.value) : "";
    }
    setDraft(next);
    setError(null);
  }, [open, itemIndex, template.items, validLocales]);

  const handleChange = (localeKey: string, value: string) => {
    setDraft((prev) => ({ ...prev, [localeKey]: value }));
  };

  const handleSave = async () => {
    if (itemIndex == null) return;
    const row = template.items?.[itemIndex];
    if (!row?.stringRegistryEntry?.documentId) return;
    setSaving(true);
    setError(null);
    try {
      const newOverrides = Object.entries(draft)
        .map(([locale, value]) => ({ locale, value: value.trim() }))
        .filter((x) => x.locale && x.value !== "");

      const res = await StringRegistryApis.upsertTemplateValues(rc, template.documentId, [
        { entryDocumentId: row.stringRegistryEntry.documentId, localeOverrides: newOverrides }
      ]);
      if (res.error) {
        setError(res.error.message || "Save failed");
        return;
      }
      onSaved();
      onClose();
    } catch (e: any) {
      setError(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const title = entry?.stringResourceId || "String";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Template overrides
        <Typography variant="body2" color="text.secondary">
          {title} — leave blank to keep the global registry value for that locale.
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {!entry ? (
          <Typography color="text.secondary">No string selected.</Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "action.hover" }}>
                  <TableCell sx={{ fontWeight: 600, width: 160, fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.5px", color: "text.secondary" }}>Locale</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.5px", color: "text.secondary" }}>Global value</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.5px", color: "text.secondary" }}>Template override</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {validLocales.map((loc) => {
                  const k = normalizeLocaleCode(loc.code);
                  const globalVal = getLocaleValue(getGlobalLocaleOverrides(entry), loc.code) || "—";
                  const hasOverride = Boolean(draft[k]?.trim());
                  return (
                    <TableRow key={k} sx={{ "&:last-child td": { border: 0 }, "&:hover": { bgcolor: "action.hover" } }}>
                      <TableCell sx={{ verticalAlign: "top", pt: 1.5 }}>
                        <Typography variant="body2" fontWeight={600}>{loc.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{k}</Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 220, verticalAlign: "top", pt: 1.5 }}>
                        <Typography
                          variant="body2"
                          color={globalVal === "—" ? "text.disabled" : "text.secondary"}
                          sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontStyle: globalVal === "—" ? "italic" : "normal" }}
                        >
                          {globalVal}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 240, verticalAlign: "top" }}>
                        <TextField
                          size="small"
                          fullWidth
                          multiline
                          minRows={1}
                          maxRows={4}
                          placeholder="Blank = inherit global"
                          value={draft[k] ?? ""}
                          onChange={(e) => handleChange(k, e.target.value)}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              bgcolor: hasOverride ? "action.selected" : "transparent"
                            }
                          }}
                        />
                        {hasOverride && (
                          <Typography variant="caption" color="primary.main" sx={{ mt: 0.5, display: "block" }}>
                            Override set
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <BglSecondarySmallButton onClick={onClose} disabled={saving}>
          Cancel
        </BglSecondarySmallButton>
        <BglPrimarySmallButton onClick={handleSave} disabled={saving || !entry}>
          {saving ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
          Save
        </BglPrimarySmallButton>
      </DialogActions>
    </Dialog>
  );
};

export default EditTemplateLocaleOverridesDialog;
